<!-- #################################################################################### -->
<!-- ###### HERINCO                                                                ###### -->
<!-- ###### @author: Estefania Villada Chavarria                                   ###### -->
<!-- ###### @date: Septiembre 2024                                                 ###### -->
<!-- #################################################################################### -->

<!-- #################################################################################### -->
<!-- ###### Sección de HTML                                                        ###### -->
<!-- #################################################################################### -->
<template>
  <div class="contenido">
    <!-- Fitros -->
    <section>
      <div class="d-flex">
        <!-- Filtrar por fecha inicial -->
        <v-menu ref="menuFechaInicio" v-model="menuFechaInicio" transition="scale-transition" min-width="290px"
          :close-on-content-click="false" :return-value.sync="filtro.fechaInicio" :nudge-top="25" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-text-field style="width:30%;" class="me-2 filtro" v-model="filtro.fechaInicio" label="Fecha inicial *" v-bind="attrs" v-on="on"
              outlined dense readonly>
            </v-text-field>
          </template>
          <v-date-picker v-model="filtro.fechaInicio" :first-day-of-week="1" :max="currentDate" locale="es-co"
            no-title scrollable @input="saveFechaInicial">
            <v-btn text color="primary" @click="cleanFechaInicial">Borrar</v-btn>
          </v-date-picker>
        </v-menu>

        <!-- Filtrar por fecha final -->
        <v-menu ref="menuFechaFinal" v-model="menuFechaFinal" transition="scale-transition" min-width="290px"
          :close-on-content-click="false" :return-value.sync="filtro.fechaFin" :nudge-top="25" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-text-field style="width:30%;" class="me-2 filtro" v-model="filtro.fechaFin" label="Fecha final *" v-bind="attrs" v-on="on"
              :error-messages="rangoFechas" outlined dense readonly>
            </v-text-field>
          </template>
          <v-date-picker v-model="filtro.fechaFin" :first-day-of-week="1" :max="currentDate" locale="es-co" no-title
            scrollable @input="saveFechaFinal">
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="cleanFechaFinal">Borrar</v-btn>
          </v-date-picker>
        </v-menu>

        <!-- Filtro por estado -->
        <v-autocomplete style="width:30%;" dense outlined label="Estado" multiple
         v-model="filtro.estadosValue" :items="itemsEstados" item-text="descripcion" item-value="id">
        </v-autocomplete>

        <!-- Botón para generar reporte -->
        <v-tooltip left :color="permitirGenerarReporte ? 'primary' : 'grey'" :open-on-focus="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn :disabled="desactivarBotonDescarga" class="ms-2" small fab :color="permitirGenerarReporte ? 'primary' : 'grey'" 
              v-bind="attrs" v-on="on" @click="permitirGenerarReporte && generarReporte()">
              <v-icon color="white"> download </v-icon>
            </v-btn>
          </template>
          <span>Generar reporte</span>
        </v-tooltip>
      </div>
    </section>

    <!-- Tabla que almacena las solicitudes -->
    <v-data-table class="elevation" :items="tablaSolicitudes.items" :loading="tablaSolicitudes.loading"
      fixed-header :headers="tablaSolicitudes.headers" :footer-props="tablaSolicitudes.footerProps"
      :items-per-page.sync="tablaSolicitudes.itemsPerPage" :page.sync="tablaSolicitudes.page"
      :server-items-length="tablaSolicitudes.totalPage" height="64vh">
      <template v-slot:[`item.fechaSolicitud`]="{ item }">
        {{ formatoFecha(item.fechaSolicitud) }}
      </template>
      <template v-slot:[`item.prioritario`]="{ item }">
        {{ item.prioritario ? 'Si' : 'No' }}
      </template>
      <template v-slot:[`item.cohorte`]="{ item }">
        {{ item.cohorte ? 'Si' : 'No' }}
      </template>
      <template v-slot:[`item.notaTecnica`]="{ item }">
        {{ item.notaTecnica ? 'Si' : 'No' }}
      </template>
      <template v-slot:[`item.autoinmune`]="{ item }">
        {{ item.autoinmune ? 'Si' : 'No' }}
      </template>
      <template v-slot:footer.page-text="items">
        {{ items.pageStart }} - {{ items.pageStop }} de {{ items.itemsLength }}
      </template>
    </v-data-table>

  </div>
</template>
<!-- #################################################################################### -->
<!-- ###### Sección de Scripts                                                     ###### -->
<!-- #################################################################################### -->
<script>
import { mapMutations, mapState } from "vuex";
import { Role } from "@/router/role.js";
import moment from "moment";

export default {
  name: "ReporteGeneralJuridica",
  data() {
    return {
      alert: false,
      rangoFechas: '',
      menuFechaInicio: false,
      menuFechaFinal: false,
      desactivarBotonDescarga: false,
      filtro: {
        fechaInicio: null,
        fechaFin: null,
        estadosValue: null
      },
      mensajeError: '',
      userRoles: {},
      roles: Role,
      tablaSolicitudes: {
        items: [],
        loading: false,
        headers: [
            { text: 'CASO', align: 'start', value: 'idSolicitud', sortable: false },
            { text: 'ESTADO', align: 'start', value: 'estado', sortable: false },
            { text: 'TIPO DOCUMENTO', align: 'center', value: 'tipoDocumento', sortable: false },
            { text: 'N° DOCUMENTO', align: 'start', value: 'numeroDocumento', sortable: false },
            { text: 'NOMBRE', align: 'center', value: 'nombreCompleto', sortable: false, width: '200px' }, // viene desglosado en el JSON
            { text: 'FECHA CREACIÓN', align: 'start', value: 'fechaCreacion', sortable: false, width: '110px' },
            { text: 'FECHA NOTIFICACIÓN', align: 'start', value: 'fechaNotificacion', sortable: false, width: '120px' },
            { text: 'FECHA LIMITE RESPUESTA', align: 'start', value: 'fechaLimiteRespuesta', sortable: false, width: '120px' },
            { text: 'CLASIFICACIÓN', align: 'center', value: 'tipoClasificacion', sortable: false, width: '110px' },
            { text: 'TIPO TRAMITE', align: 'start', value: 'descripcionTipoClasificacion', sortable: false },
            { text: 'MOTIVO', align: 'start', value: 'descripcionMotivo', sortable: false },
            { text: 'CANAL INGRESO', align: 'start', value: 'canalIngreso', sortable: false },
            { text: 'ASEGURADORA', align: 'start', value: 'nombreAseguradora', sortable: false },
            { text: 'CIUDAD', align: 'start', value: 'ciudad', sortable: false },
            { text: 'NUMERO CONTACTO', align: 'start', value: 'numeroContacto', sortable: false },
            { text: 'RADICADO EXTERNO', align: 'start', value: 'radicadoExterno', sortable: false }
        ],
        footerProps: {
        'items-per-page-options': [10, 20, 30, 40, 50],
        'items-per-page-text': 'Items por página:',
        'show-current-page': true,
        'show-first-last-page': true
        },
      itemsPerPage: 10,
      page: 1,
      totalPage: 0,
      },
      itemsEstados: [],
      rules: {
        required: [
          v => !!v || "Este campo es requerido.",
        ],
      },
    }
  },
  mounted() {
    this.listarSolicitudes();
    this.listarEstados();
  },
  computed: {
    ...mapState(["auth", "notify", "busy"]),
    /**
     * Función computada que devuelve la fecha actual en formato ISO (YYYY-MM-DD)
     * para que en el v-date-picker no seleccione fechas posteriores a las del día actual. 
     */
    currentDate() {
      return new Date().toISOString().split('T')[0];
    },
    /**
     * Valida que los filtros estén llenos y hayan resultados para permitir generar el reporte.
     */
    permitirGenerarReporte() {
        const { fechaInicio, fechaFin } = this.filtro;
        const { items } = this.tablaSolicitudes;

        const tieneFechasValidas = fechaInicio && fechaFin;
        const tieneItems = items?.length > 0;

        if (!tieneFechasValidas) return false;

        // Caso alternativo: cuando no hay rango de fechas pero sí items
        return tieneFechasValidas && tieneItems && !this.rangoFechas;
        }

  },
  watch: {
    /**
     * Este watcher se activa cuando alguna propiedad del objeto `filtro` cambia.
     * Es "deep", por lo que observa cambios en propiedades anidadas dentro de `filtro`.
     * Al detectar cambios, se encarga de desactivar la alerta (`alert` se establece en false).
     */
    'filtro.fechaInicio' : function () {
      if ((this.filtro.fechaInicio && this.filtro.fechaFin) || (!this.filtro.fechaInicio && !this.filtro.fechaFin)) {
        this.tablaSolicitudes.page = 1;
        this.listarSolicitudes();
      }
    },
    'filtro.fechaFin' : function () {
      if ((this.filtro.fechaInicio && this.filtro.fechaFin) || (!this.filtro.fechaInicio && !this.filtro.fechaFin)) {
        this.tablaSolicitudes.page = 1;
        this.listarSolicitudes();
      }
    },
    'filtro.estadosValue' : function () {
        this.listarSolicitudes();
    },
    'tablaSolicitudes.page': function () {
      this.listarSolicitudes();
    },
    'tablaSolicitudes.itemsPerPage': function () {
      this.tablaSolicitudes.page = 1;
      this.listarSolicitudes(); 
    }
  },
  methods: {
    ...mapMutations([
      "updateAuth",
      "hideNotify",
      "showSuccess",
      "showBusy",
      "hideBusy",
    ]),
    /**
     *  Método que lista las solicitudes en la tabla.
     */
    listarSolicitudes() {
      if (this.rangoFechas === '') {
        this.$http.get(`msa-reports/api/v1/juridica/listar`, {
          params: {
            page: this.tablaSolicitudes.page -1,
            size: this.tablaSolicitudes.itemsPerPage,
            estados: this.filtro.estadosValue != null ? this.filtro?.estadosValue.join(',') : null,
            fechaInicio: this.filtro.fechaInicio ? this.filtro.fechaInicio : null,
            fechaFin: this.filtro.fechaFin ? this.filtro.fechaFin : null
          }
        }).then(response => {
          this.tablaSolicitudes.items = response.data.content;
          this.tablaSolicitudes.totalPage = response.data.totalElements;          
        }).catch(error => {
          console.log(error);
        })
      }
    },
    /**
     * Este método verifica que la fecha inicial (`fechaUno`) sea anterior a la fecha final (`fechaDos`). Además, 
     * se asegura de que el rango de fechas seleccionadas no exceda un mes. Si alguna de estas condiciones no se cumple, 
     * establece un mensaje de error correspondiente y marca el estado como inválido (`invalid = true`).
     */
    validacionesFechas() {
        const fechaUno = new Date(`${this.filtro.fechaInicio}T00:00:00-05:00`);
        const fechaDos = new Date(`${this.filtro.fechaFin}T00:00:00-05:00`);

        // Validar que la fecha inicial sea menor o igual a la final
        if (fechaUno > fechaDos) {
            this.rangoFechas = 'La fecha final debe ser mayor o igual a la fecha inicial';
            return;
        }

        // Calcular rango de un mes a partir de la fecha inicial
        let rangoUnMes = new Date(fechaUno);
        rangoUnMes.setMonth(rangoUnMes.getMonth() + 1);

        // Si el día de inicio es el 1, ajustar para que el rango sea hasta el último día del mismo mes
        if (fechaUno.getDate() === 1) {
            rangoUnMes.setMonth(rangoUnMes.getMonth(), 0); // último día del mes de inicio
        }

        // Validar que la fecha final no supere el rango de un mes
        if (fechaDos > rangoUnMes) {
            this.rangoFechas = 'El rango no puede ser mayor a un mes';
            return;
        }

        // Si todo está bien
        this.rangoFechas = '';
    },

    /**
     * Método que guarda la fecha inicial en el campo de entrada.
     */
    saveFechaInicial() {
      this.$refs.menuFechaInicio.save(this.filtro.fechaInicio);
      this.validacionesFechas();
    },
    /**
     * Método que guarda la fecha final en el campo de entrada.
     */
    saveFechaFinal() {
      this.$refs.menuFechaFinal.save(this.filtro.fechaFin);
      this.validacionesFechas();
    },
    /**
     * Método que limpia el campo de entrada del filtro fecha inicial.
     */
    cleanFechaInicial() {
      this.filtro.fechaInicio = null;
      this.$refs.menuFechaInicio.save('');
      this.rangoFechas = '';
    },
    /**
     * Método que limpia el campo de entrada del filtro fecha final.
     */
    cleanFechaFinal() {
      this.filtro.fechaFin = null;
      this.$refs.menuFechaFinal.save('');
      this.rangoFechas = '';
    },
    /**
     * Método que formatea una fecha dada en el formato 'DD/MM/YYYY hh:mm:ss a'.
     * @param fecha - La fecha que se va a formatear.
     */
    formatoFecha(fecha) {
      return moment(fecha).format('DD/MM/YYYY hh:mm:ss a');
    },
    /**
     * Método para generar y descargar un archivo CSV con los datos de las solicitudes de ruta.
     */
     generarReporte() {
      this.desactivarBotonDescarga = true;
      this.$http.get(`msa-reports/api/v1/juridica/descargar`, {
        responseType: 'blob',
        params: {
          fechaInicio: this.filtro.fechaInicio ? this.filtro.fechaInicio : null,
          fechaFin: this.filtro.fechaFin ? this.filtro.fechaFin : null,
          estados: this.filtro?.estadosValue?.length ? this.filtro.estadosValue.join(',') : "",
          page: 0,
          size: this.tablaSolicitudes.totalPage
        }
      }).then(response => {
        const filename = `reporte_general_juridica_${this.filtro.fechaInicio.replace(/-/g, '_')}_al_${this.filtro.fechaFin.replace(/-/g, '_')}.xlsx`;
        
        // Crear un objeto URL para el Blob
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        
        // Crear un enlace de descarga
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        
        // Limpiar el objeto URL después de la descarga
        window.URL.revokeObjectURL(url);
        this.desactivarBotonDescarga = false;
      }).catch(error => {
        this.desactivarBotonDescarga = false;
        console.log(error);
      });
    },

    listarEstados() {
      if (this.rangoFechas === '') {
        this.$http.get(`msa-reports/api/v1/juridica/listar/estados`).then(response => {
          this.itemsEstados = response.data;       
        }).catch(error => {
          console.log(error);
        })
      }
    },
  }
}
</script>
<!-- #################################################################################### -->
<!-- ###### Sección de Estilos                                                     ###### -->
<!-- #################################################################################### -->
<style scoped>
.contenido {
  padding: 1rem;
  width: 100%;
  height: 86vh;
}

::v-deep .elevation div table thead tr th {
  background-color: rgb(223, 223, 223) !important;
}

::v-deep .elevation div table thead tr th span {
  font-weight: bold;
  color: black !important;
}

.filtro {
  width: 50%;
}
</style>
